import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, useTheme } from '@mui/material';
import DividerConTitulo from './DividerConTitulo';
import RichTextBlock from './RichTextBlock';
import InfoCardStack from './InfoCardStack';
import { fetchCardsGroup } from '../utils/contentfulRequests';

/**
 * InfoCardGroup
 * Obtiene un grupo de tarjetas informativas desde Contentful por slug y renderiza
 * un `InfoCardStack` con el contenido. Si no se encuentra nada, muestra null.
 * Props:
 * - slug: string (opcional) - slug del grupo en Contentful, por ejemplo 'nosotros'
 */
export default function InfoCardGroup({ slug, allowMultipleOpen = false, group: propGroup = null }) {
  const [group, setGroup] = useState(propGroup);
  const [loading, setLoading] = useState(!propGroup);

  useEffect(() => {
    if (propGroup) return; // si ya viene el group por props, no hacemos fetch
    let mounted = true;
    const load = async () => {
      setLoading(true);
      const g = await fetchCardsGroup(slug);
      if (!mounted) return;
      setGroup(g);
      setLoading(false);
    };

    load();
    return () => {
      mounted = false;
    };
  }, [slug, propGroup]);

  if (loading) return <Typography>Cargando...</Typography>;
  if (!group) return null;

  const cards = (group.items || []).map((it) => ({
    id: it.id,
    title: it.titulo || it.title || '',
    subtitle: it.subtitulo || it.subtitle || '',
    content:
      typeof it.contenido === 'object' && it.contenido !== null
        ? <RichTextBlock content={it.contenido} />
        : it.contenido || '',
  }));

  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 4 },
        background: theme.palette.background.component,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ maxWidth: 1000, width: '100%' }}>
        {group.titulo && <DividerConTitulo titulo={group.titulo} />}
        <Box sx={{ mt: 2 }}>
          <InfoCardStack cards={cards} allowMultipleOpen={allowMultipleOpen} />
        </Box>
      </Box>
    </Box>
  );
}

InfoCardGroup.propTypes = {
  slug: PropTypes.string,
  allowMultipleOpen: PropTypes.bool,
  group: PropTypes.shape({
    titulo: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        titulo: PropTypes.string,
        contenido: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      })
    ),
  }),
};
